import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, Download, ArrowRight, CheckCircle } from 'lucide-react';

const TOOLKIT_COVER = 'https://customer-assets-lqy194kg.emergentagent.net/job_a458f44e-02cd-4e55-83d8-3e9684068794/artifacts/e6vo0d5c_1CAFEF48-974C-4AFB-8229-26CBF6DE8A38.png';

const HIGHLIGHTS = [
  'Vehicle inspection checklists',
  'Questions to ask any seller',
  'Dealer & private-seller red flags',
  'A negotiation worksheet that works',
];

// Reusable "Not ready to book? Get the $19 Toolkit" promotion banner.
export const ToolkitPromo = ({ variant = 'full' }) => {
  const navigate = useNavigate();

  if (variant === 'compact') {
    return (
      <div
        className="rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-orange-600/10 p-6 md:p-7 flex flex-col sm:flex-row items-center gap-5"
        data-testid="toolkit-promo-compact"
      >
        <div className="w-12 h-12 rounded-xl bg-orange-500/15 flex items-center justify-center shrink-0">
          <BookOpen className="w-6 h-6 text-orange-400" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="text-white font-bold text-lg" style={{ fontFamily: 'Space Grotesk' }}>Not ready to book yet?</p>
          <p className="text-zinc-300 text-sm mt-1">
            Grab the <strong className="text-white">Ultimate Used Car Buyer Toolkit&trade;</strong> and shop smarter today &mdash; just $19.
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-lg shrink-0"
          onClick={() => navigate('/used-car-buyer-toolkit')}
          data-testid="toolkit-promo-compact-btn"
        >
          Get the $19 Toolkit <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="toolkit-promo">
      <div className="rounded-3xl border border-orange-500/30 bg-gradient-to-br from-[#161618] to-[#0e0e10] overflow-hidden">
        <div className="grid md:grid-cols-2 gap-8 items-center p-8 md:p-12">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 mb-5">
              <Download className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-semibold tracking-wide">Digital Toolkit &bull; $19 &bull; Instant Access</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Not Ready for an Inspection Yet?
            </h2>
            <p className="mt-4 text-zinc-300 text-base md:text-lg leading-relaxed">
              Shop smarter with the <strong className="text-white">CarLens Ultimate Used Car Buyer Toolkit&trade;</strong> &mdash; your step-by-step guide to spotting red flags, asking the right questions, and knowing when to walk away.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {HIGHLIGHTS.map((h, i) => (
                <li key={i} className="flex items-start gap-2.5 text-zinc-200 text-sm" data-testid={`toolkit-promo-item-${i}`}>
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <Button
              size="lg"
              className="mt-8 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20"
              onClick={() => navigate('/used-car-buyer-toolkit')}
              data-testid="toolkit-promo-btn"
            >
              Get Instant Access &mdash; $19
            </Button>
          </div>
          <div className="relative mx-auto w-full max-w-xs">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/25 to-zinc-600/20 rounded-3xl blur-2xl" />
            <img
              src={TOOLKIT_COVER}
              alt="CarLens Ultimate Used Car Buyer Toolkit cover"
              className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToolkitPromo;
